import type { FC } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Security as SecurityIcon,
  AccountBalance as AccountBalanceIcon,
  Assessment as ReportsIcon,
  Email as EmailIcon,
  Check as CheckIcon,
  Extension as ExtensionIcon,
} from '@mui/icons-material';
import CTAButton from '../components/common/CTAButton';

const FeaturesPage: FC = () => {
  const featureCategories = [
    {
      title: 'VoIP & Telephony',
      icon: PhoneIcon,
      description: 'Complete phone system built for collections',
      features: [
        'SIP.js softphone in browser',
        'Predictive dialer with compliance checks',
        'Call recording with encryption',
        'Voicemail management',
        'Click-to-call from any screen',
        'TCPA-compliant time zone restrictions',
        'Do Not Call list integration',
        'Call disposition tracking',
      ],
    },
    {
      title: 'Account Management',
      icon: AccountBalanceIcon,
      description: 'Comprehensive debt account tracking',
      features: [
        'Full debtor profile management',
        'Payment plan creation & tracking',
        'Settlement calculator',
        'Interest & fee calculations',
        'Account assignment & queues',
        'Linked account grouping',
        'Status workflow automation',
        'Skip tracing integration',
      ],
    },
    {
      title: 'Compliance & Audit',
      icon: SecurityIcon,
      description: 'Stay compliant, always',
      features: [
        'FDCPA compliance tracking',
        'TCPA compliance automation',
        'State-level regulation support',
        'Complete audit trail',
        'Letter template validation',
        'Communication logging',
        'Dispute management',
        'Compliance officer dashboard',
      ],
    },
    {
      title: 'Communications',
      icon: EmailIcon,
      description: 'Multi-channel outreach',
      features: [
        'Email with template system',
        'SMS messaging (Twilio integration)',
        'Postal mail (Lob.com integration)',
        'QR code payment letters',
        'Automated dunning sequences',
        'Communication preferences',
        'Consent management',
        'Template personalization',
      ],
    },
    {
      title: 'Reports & Analytics',
      icon: ReportsIcon,
      description: 'Data-driven decisions',
      features: [
        'Real-time collector metrics',
        'Recovery rate analysis',
        'Account aging reports',
        'Payment trend charts',
        'Compliance scorecards',
        'Executive dashboards',
        'Export to Excel/PDF',
        'Scheduled report delivery',
      ],
    },
    {
      title: 'Integrations',
      icon: ExtensionIcon,
      description: 'Connect everything',
      features: [
        'Credit bureau reporting',
        'Payment gateway integration',
        'Skip tracing services',
        'CRM synchronization',
        'REST API access',
        'Webhook notifications',
        'Module marketplace',
        'Custom module SDK',
      ],
    },
  ];

  const securityFeatures = [
    'AES-256 encryption for all PII',
    'Argon2 password hashing',
    'JWT with refresh token rotation',
    'CSRF protection',
    'Rate limiting',
    'Role-based access control',
    'Complete audit logging',
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          py: 12,
          background: 'radial-gradient(ellipse at 50% 0%, rgba(100, 181, 246, 0.15) 0%, transparent 70%)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h1" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, mb: 3 }}>
              Features Built for
              <Box
                component="span"
                sx={{
                  display: 'block',
                  background: 'linear-gradient(135deg, #64b5f6 0%, #f48fb1 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Collection Excellence
              </Box>
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 4 }}>
              Every tool you need to collect efficiently while staying fully compliant.
              Built by collection professionals, for collection professionals.
            </Typography>
            <CTAButton to="/request-demo">
              Try the Live Demo
            </CTAButton>
          </Box>
        </Container>
      </Box>

      {/* Feature Categories */}
      <Box sx={{ py: 12 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {featureCategories.map((category, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #64b5f6 0%, #f48fb1 100%)',
                        }}
                      >
                        <category.icon sx={{ fontSize: 28, color: 'white' }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight={600}>
                          {category.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {category.description}
                        </Typography>
                      </Box>
                    </Box>
                    <List dense>
                      {category.features.map((feature, idx) => (
                        <ListItem key={idx} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckIcon sx={{ fontSize: 18, color: 'success.main' }} />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Security Section */}
      <Box sx={{ py: 12, bgcolor: 'background.paper' }} id="security">
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" gutterBottom>
                Security by Design
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                Every record is encrypted, every action is audited, and access
                is controlled by role. Security and compliance are built in, not
                bolted on.
              </Typography>
              <List>
                {securityFeatures.map((feature, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <SecurityIcon sx={{ color: 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 4,
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, rgba(100, 181, 246, 0.1) 0%, rgba(244, 143, 177, 0.1) 100%)',
                  border: '1px solid rgba(100, 181, 246, 0.3)',
                  textAlign: 'center',
                }}
              >
                <SecurityIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                <Typography variant="h4" gutterBottom>
                  Encrypted by Default
                </Typography>
                <Typography color="text.secondary">
                  All sensitive PII is encrypted with AES-256, and every
                  consumer interaction is captured in a complete, immutable
                  audit trail.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 12 }}>
        <Container maxWidth="md">
          <Card
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(100, 181, 246, 0.1) 0%, rgba(244, 143, 177, 0.1) 100%)',
              border: '1px solid rgba(100, 181, 246, 0.3)',
            }}
          >
            <Typography variant="h3" gutterBottom>
              See It In Action
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Explore a live, fully-seeded demo — including the compliance engine in action.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <CTAButton to="/request-demo">
                Try the Live Demo
              </CTAButton>
              <CTAButton to="/contact" variant="outlined">
                Contact Us
              </CTAButton>
            </Box>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default FeaturesPage;
